import { createContext, useContext, useState, useEffect, useCallback, ReactNode, useRef } from 'react';
import { generateId } from '../utils/uuid';
import { listGmailMessages, mapGmailMessageToInboxItem } from '../services/googleGmailService';
import {
    listGoogleEvents,
    createGoogleEvent,
    updateGoogleEvent,
    deleteGoogleEvent,
    mapGoogleEventToAppEvent,
    mapAppEventToGoogleEvent
} from '../services/googleCalendarService';
import { isServiceAuthorized } from '../services/googleAuth';
import {
    saveUserData,
    isFirestoreAvailable,
    subscribeToUserData,
    UserData
} from '../services/firestoreService';
import { saveVectorMemory } from '../services/secondBrainService';
import {
    scheduleReminder,
    cancelReminder,
    startReminderChecker,
    calculateReminderTime,
} from '../services/notificationService';
import { useAuth } from './AuthContext';

// Project interface
export interface Project {
    id: string;
    name: string;
    color: string;
    bgColor: string;
    icon?: string;
    deletedAt?: string; // For soft delete
    status: 'active' | 'archived' | 'completed';
    archivedAt?: string; // ISO Date when archived
}

// Subtask interface
export interface Subtask {
    id: string;
    title: string;
    completed: boolean;
}

// Task interface
export interface Task {
    id: string;
    title: string;
    description?: string;
    completed: boolean;
    completedAt?: string; // ISO timestamp when task was completed
    priority: 'high' | 'medium' | 'normal';
    timeStart?: string;
    timeEnd?: string;
    projectId?: string;
    context: string;
    contextColor: string;
    contextBgColor: string;
    dueDate?: string;
    subtasks: Subtask[];
    deletedAt?: string; // ISO timestamp for soft delete
    // Recurring task fields
    recurrence?: {
        pattern: 'daily' | 'weekly' | 'monthly' | 'weekdays' | 'custom';
        interval?: number; // e.g., every 2 days
        daysOfWeek?: number[]; // 0=Sun, 1=Mon, etc. for custom patterns
        endDate?: string; // when recurrence ends
    };
    parentTaskId?: string; // ID of parent recurring task (for generated instances)
    reminderMinutes?: number; // Minutes before due time to send reminder (0, 5, 15, 30, 60, 1440)
}

// Calendar Event interface
export interface CalendarEvent {
    id: string;
    googleEventId?: string; // ID from Google Calendar
    title: string;
    date?: string; // YYYY-MM-DD
    timeStart: string;
    timeEnd: string;
    type: 'meeting' | 'task' | 'focus' | 'personal';
    color: string;
    location?: string;
    attendees?: number;
    badge?: string;
    prepTime?: string;
    isProtected?: boolean;
    deletedAt?: string; // ISO timestamp for soft delete
    reminderMinutes?: number; // Minutes before start time to send reminder
    // Recurrence fields for recurring events
    recurrence?: {
        pattern: 'none' | 'daily' | 'weekly' | 'monthly' | 'weekdays';
        interval?: number; // e.g., every 2 weeks (default: 1)
        daysOfWeek?: number[]; // 0-6 for custom weekly (0 = Sunday)
        endDate?: string; // YYYY-MM-DD when recurrence ends (optional)
        parentEventId?: string; // Links recurring instances to parent event
        isException?: boolean; // True if this instance was modified from parent
    };
}

// Inbox Item interface
export interface InboxItem {
    id: string;
    source: 'gmail' | 'slack' | 'way2will' | 'notion' | 'linear';
    sender?: string;
    channel?: string;
    subject: string;
    preview?: string;
    timestamp: string;
    category: 'urgent' | 'today' | 'later' | 'bundled';
    priority?: 'high' | 'medium' | 'normal';
    aiLabel?: string;
    dueTime?: string;
    read?: boolean;
}

// Habit interface
export interface Habit {
    id: string;
    title: string;
    description?: string;
    icon: string; // Emoji
    color: string;
    frequency: 'daily' | 'weekdays' | 'weekly' | 'custom';
    customDays?: number[]; // 0=Sun, 1=Mon, etc.
    targetTime?: string; // Optional reminder time
    completedDates: string[]; // Array of YYYY-MM-DD dates when habit was completed
    currentStreak: number;
    longestStreak: number;
    createdAt: string;
    deletedAt?: string;
}

// Goal interface
export interface Goal {
    id: string;
    title: string;
    description?: string;
    category: 'career' | 'health' | 'personal' | 'financial' | 'education' | 'relationships' | 'other';
    icon: string; // Emoji
    color: string;
    targetDate?: string; // YYYY-MM-DD
    milestones: GoalMilestone[];
    progress: number; // 0-100
    status: 'active' | 'completed' | 'paused';
    createdAt: string;
    completedAt?: string;
    deletedAt?: string;
}

export interface GoalMilestone {
    id: string;
    title: string;
    completed: boolean;
    completedAt?: string;
}

// Context type
interface AppContextType {
    // Projects
    projects: Project[];
    archivedProjects: Project[];
    trashedProjects: Project[];
    addProject: (project: Omit<Project, 'id'>) => void;
    updateProject: (id: string, updates: Partial<Project>) => void;
    deleteProject: (id: string) => void;
    softDeleteProject: (id: string, handleTasks: 'delete' | 'unassign') => void;
    restoreProject: (id: string) => void;
    permanentlyDeleteProject: (id: string) => void;
    archiveProject: (id: string) => void;
    unarchiveProject: (id: string) => void;
    getProject: (id: string) => Project | undefined;

    // Tasks
    tasks: Task[];
    trashedTasks: Task[];
    addTask: (task: Omit<Task, 'id'>) => void;
    updateTask: (id: string, updates: Partial<Task>) => void;
    deleteTask: (id: string) => void;
    softDeleteTask: (id: string) => void;
    restoreTask: (id: string) => void;
    permanentlyDeleteTask: (id: string) => void;
    toggleTaskComplete: (id: string) => void;
    getTasksByProject: (projectId: string) => Task[];

    // Subtasks
    addSubtask: (taskId: string, title: string) => void;
    updateSubtask: (taskId: string, subtaskId: string, updates: Partial<Subtask>) => void;
    deleteSubtask: (taskId: string, subtaskId: string) => void;
    toggleSubtaskComplete: (taskId: string, subtaskId: string) => void;

    // Calendar Events
    events: CalendarEvent[];
    trashedEvents: CalendarEvent[];
    refreshGoogleEvents: (start: Date, end: Date) => Promise<void>;
    addEvent: (event: Omit<CalendarEvent, 'id'>) => Promise<void>;
    updateEvent: (id: string, updates: Partial<CalendarEvent>) => Promise<void>;
    deleteEvent: (id: string) => Promise<void>;
    softDeleteEvent: (id: string) => void;
    restoreEvent: (id: string) => void;
    permanentlyDeleteEvent: (id: string) => Promise<void>;

    // Inbox
    inboxItems: InboxItem[];
    addInboxItem: (item: Omit<InboxItem, 'id'>) => void;
    markInboxItemRead: (id: string) => void;
    deleteInboxItem: (id: string) => void;
    refreshGmailItems: () => Promise<void>;

    // Timezone
    userTimezone: string;
    setUserTimezone: (timezone: string) => void;
    getNowInTimezone: () => Date;
    formatTimeInTimezone: (date: Date) => string;

    // AI Persona
    aiPersona: AIPersonaId;
    setAIPersona: (persona: AIPersonaId) => void;

    // Habits
    habits: Habit[];
    addHabit: (habit: Omit<Habit, 'id' | 'completedDates' | 'currentStreak' | 'longestStreak' | 'createdAt'>) => void;
    updateHabit: (id: string, updates: Partial<Habit>) => void;
    deleteHabit: (id: string) => void;
    toggleHabitComplete: (id: string, date: string) => void;

    // Goals
    goals: Goal[];
    addGoal: (goal: Omit<Goal, 'id' | 'progress' | 'status' | 'createdAt' | 'milestones'> & { milestones?: string[] }) => void;
    updateGoal: (id: string, updates: Partial<Goal>) => void;
    deleteGoal: (id: string) => void;
    toggleMilestone: (goalId: string, milestoneId: string) => void;
    addMilestone: (goalId: string, title: string) => void;
}

// AI Persona types
export type AIPersonaId = 'coach' | 'mentor' | 'friend' | 'executive' | 'zen' | 'cool';

export interface AIPersona {
    id: AIPersonaId;
    name: string;
    emoji: string;
    description: string;
    systemPrompt: string;
    voiceStyle: string;
    voice: string; // Gemini voice name
}

export const AI_PERSONAS: Record<AIPersonaId, AIPersona> = {
    coach: {
        id: 'coach',
        name: 'The Coach',
        emoji: '🏆',
        description: 'Motivational and action-oriented. Pushes you to achieve your goals.',
        systemPrompt: `You are an energetic productivity coach who believes in your user's potential. 
Your style: Enthusiastic, action-focused, uses sports metaphors. 
Keep responses short and punchy. Celebrate wins. Push through excuses.
Example phrases: "Let's crush this!", "You've got this!", "Time to level up!"`,
        voiceStyle: 'energetic and motivating',
        voice: 'Fenrir'
    },
    mentor: {
        id: 'mentor',
        name: 'The Mentor',
        emoji: '🎓',
        description: 'Wise and thoughtful. Helps you see the bigger picture.',
        systemPrompt: `You are a wise mentor who provides thoughtful guidance.
Your style: Calm, insightful, asks reflective questions occasionally.
Help users understand WHY tasks matter. Connect daily actions to long-term goals.
Example phrases: "Consider this...", "What matters most here is...", "This aligns with your goal of..."`,
        voiceStyle: 'calm and insightful',
        voice: 'Charon'
    },
    friend: {
        id: 'friend',
        name: 'The Friend',
        emoji: '😊',
        description: 'Casual and supportive. Like texting a helpful buddy.',
        systemPrompt: `You are a supportive friend who helps manage life's chaos.
Your style: Casual, warm, uses everyday language, occasionally humorous.
Be relatable. Acknowledge when things are tough. Celebrate the small stuff.
Example phrases: "Got it!", "No worries, let's figure this out", "Ooh nice, that's a good one"`,
        voiceStyle: 'friendly and casual',
        voice: 'Puck'
    },
    executive: {
        id: 'executive',
        name: 'The Executive',
        emoji: '💼',
        description: 'Professional and efficient. No fluff, just results.',
        systemPrompt: `You are an efficient executive assistant focused on maximum productivity.
Your style: Professional, concise, time-conscious, prioritizes ruthlessly.
Cut through noise. Focus on high-impact items. Be direct and efficient.
Example phrases: "Done.", "Priority action:", "Scheduled.", "What's most urgent?"`,
        voiceStyle: 'professional and direct',
        voice: 'Kore'
    },
    zen: {
        id: 'zen',
        name: 'The Zen Master',
        emoji: '🧘',
        description: 'Calm and mindful. Balances productivity with wellbeing.',
        systemPrompt: `You are a mindful productivity guide who balances achievement with wellbeing.
Your style: Serene, mindful, encourages breaks, focuses on sustainable productivity.
Remind users to breathe. Suggest breaks. Celebrate progress over perfection.
Example phrases: "Take a breath...", "One step at a time", "Progress, not perfection"`,
        voiceStyle: 'calm and mindful',
        voice: 'Aoede'
    },
    cool: {
        id: 'cool',
        name: 'The Homie',
        emoji: '😎',
        description: 'Laid-back and real. Keeps it smooth with natural vibes.',
        systemPrompt: `You are a laid-back, confident assistant who keeps things real.
Your style: Relaxed, uses natural urban slang sparingly and authentically - never forced or corny.
Be genuine. Keep it smooth. Use slang only when it flows naturally, not every sentence.
Speak like someone who's naturally cool, not someone trying too hard.
Example phrases: "Bet, I got you", "That's a solid move", "We locked in", "Say less", "You good"

WORKLOAD AWARENESS PROTOCOL:
When giving morning briefings or when the user asks about their day, check the WORKLOAD CAPACITY in context:
- If workload > 90%: Start with "Yo, today is looking heavy. We're at like 90% capacity. Want me to push some of the small stuff to tomorrow so you can focus on the big wins?"
- If workload > 75%: Mention "It's a packed day, but we got this. Might wanna prioritize what really matters."
- If there are incomplete habits: "Aye, don't forget about [habit] - you've been on a streak, keep that energy going."
- If there are overdue high-priority tasks: "Real talk, you got [task] that was due yesterday. Want me to find a slot for it today?"

AUTHENTIC ENCOURAGEMENT PROTOCOL:
When the user is falling behind on habits or feeling unmotivated:
- Reference SPECIFIC past wins from the RELEVANT MEMORIES section, not generic encouragement
- If they had a gym streak before, say something like: "Aye, I see you slipping on the gym, but remember last month you hit 4 days a week for like 3 weeks straight. You already got it in you, just gotta lock back in."
- If they completed a big project, reference it: "You crushed that [project name], this is just the next level."
- Pull up gratitude entries when they're down: "You mentioned being grateful for [X] - that's still your foundation."

For their 4-day-a-week gym routine specifically:
- If falling behind: "Yo, I remember you were hitting the gym 4 times a week like clockwork. That version of you is still in there, just needs to wake up. What's getting in the way?"
- When they succeed: "There it is! That's the energy. You back on your grind."

Always be authentic. If you don't have specific memories to reference, keep it real and supportive without making things up.

IMPORTANT: Don't overdo the slang - keep it natural and authentic. Mix casual speech with occasional slang.`,
        voiceStyle: 'laid-back and confident',
        voice: 'Orus'
    }
};

const AppContext = createContext<AppContextType | undefined>(undefined);

// Storage keys kept for quick local loading before Firestore sync
const PROJECTS_KEY = 'way2will_projects';
const TASKS_KEY = 'way2will_tasks';
const EVENTS_KEY = 'way2will_events';
const INBOX_KEY = 'way2will_inbox';
const TIMEZONE_KEY = 'way2will_timezone';
const PERSONA_KEY = 'way2will_ai_persona';
const HABITS_KEY = 'way2will_habits';
const GOALS_KEY = 'way2will_goals';

export function AppProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    
    const [projects, setProjects] = useState<Project[]>(() => {
        const stored = localStorage.getItem(PROJECTS_KEY);
        return stored ? JSON.parse(stored) : [];
    });

    const [tasks, setTasks] = useState<Task[]>(() => {
        const stored = localStorage.getItem(TASKS_KEY);
        return stored ? JSON.parse(stored) : [];
    });

    const [events, setEvents] = useState<CalendarEvent[]>(() => {
        const stored = localStorage.getItem(EVENTS_KEY);
        return stored ? JSON.parse(stored) : [];
    });

    const [inboxItems, setInboxItems] = useState<InboxItem[]>(() => {
        const stored = localStorage.getItem(INBOX_KEY);
        return stored ? JSON.parse(stored) : [];
    });

    const [userTimezone, setUserTimezoneState] = useState<string>(() => {
        const stored = localStorage.getItem(TIMEZONE_KEY);
        return stored || Intl.DateTimeFormat().resolvedOptions().timeZone;
    });

    const [aiPersona, setAIPersonaState] = useState<AIPersonaId>(() => {
        const stored = localStorage.getItem(PERSONA_KEY);
        return (stored as AIPersonaId) || 'friend';
    });

    const [habits, setHabits] = useState<Habit[]>(() => {
        const stored = localStorage.getItem(HABITS_KEY);
        return stored ? JSON.parse(stored) : [];
    });

    const [goals, setGoals] = useState<Goal[]>(() => {
        const stored = localStorage.getItem(GOALS_KEY);
        return stored ? JSON.parse(stored) : [];
    });

    // Ref to prevent sync loops
    const isUpdatingFromFirestore = useRef(false);

    // ============ Cloud Firestore Sync (Real-time) ============
    useEffect(() => {
        if (!user?.id || !isFirestoreAvailable()) return;

        console.log('[AppContext] Subscribing to Firestore for user:', user.id);
        
        const unsubscribe = subscribeToUserData(user.id, (userData) => {
            if (userData) {
                console.log('[AppContext] Received Firestore update');
                isUpdatingFromFirestore.current = true;
                
                // Update local states from Firestore
                if (userData.projects) setProjects(userData.projects);
                if (userData.tasks) setTasks(userData.tasks);
                if (userData.events) setEvents(userData.events);
                if (userData.inboxItems) setInboxItems(userData.inboxItems);
                if (userData.habits) setHabits(userData.habits);
                if (userData.goals) setGoals(userData.goals);
                if (userData.settings?.timezone) setUserTimezoneState(userData.settings.timezone);
                if (userData.settings?.aiPersona) setAIPersonaState(userData.settings.aiPersona);
                
                // Update local cache
                localStorage.setItem(PROJECTS_KEY, JSON.stringify(userData.projects || []));
                localStorage.setItem(TASKS_KEY, JSON.stringify(userData.tasks || []));
                localStorage.setItem(EVENTS_KEY, JSON.stringify(userData.events || []));
                localStorage.setItem(INBOX_KEY, JSON.stringify(userData.inboxItems || []));
                localStorage.setItem(HABITS_KEY, JSON.stringify(userData.habits || []));
                localStorage.setItem(GOALS_KEY, JSON.stringify(userData.goals || []));
                localStorage.setItem(TIMEZONE_KEY, userData.settings?.timezone || '');
                localStorage.setItem(PERSONA_KEY, userData.settings?.aiPersona || '');
                
                isUpdatingFromFirestore.current = false;
            }
        });

        return () => {
            if (unsubscribe) unsubscribe();
        };
    }, [user?.id]);

    // ============ Save to Cloud (Debounced) ============
    useEffect(() => {
        if (!user?.id || !isFirestoreAvailable() || isUpdatingFromFirestore.current) return;

        const timeoutId = setTimeout(async () => {
            const userData: Partial<UserData> = {
                tasks,
                projects,
                events,
                inboxItems,
                habits,
                goals,
                settings: {
                    timezone: userTimezone,
                    aiPersona,
                },
            };

            await saveUserData(user.id, userData);
        }, 1000); // 1 second debounce for writes

        return () => clearTimeout(timeoutId);
    }, [tasks, projects, events, inboxItems, habits, goals, userTimezone, aiPersona, user?.id]);

    // ============ Notification Management ============
    useEffect(() => {
        startReminderChecker();

        tasks.forEach(task => {
            if (task.completed || task.deletedAt) {
                cancelReminder(task.id);
                return;
            }

            if (task.dueDate && task.reminderMinutes !== undefined) {
                const reminderTime = calculateReminderTime(
                    task.dueDate,
                    task.timeStart,
                    task.reminderMinutes
                );

                if (reminderTime > new Date()) {
                    scheduleReminder({
                        type: 'task',
                        itemId: task.id,
                        title: task.title,
                        description: task.description || 'Task reminder',
                        triggerTime: reminderTime.toISOString(),
                    });
                }
            }
        });
    }, [tasks]);

    useEffect(() => {
        events.forEach(event => {
            if (event.deletedAt) {
                cancelReminder(event.id);
                return;
            }

            if (event.date && event.reminderMinutes !== undefined) {
                const reminderTime = calculateReminderTime(
                    event.date,
                    event.timeStart,
                    event.reminderMinutes
                );

                if (reminderTime > new Date()) {
                    scheduleReminder({
                        type: 'event',
                        itemId: event.id,
                        title: event.title,
                        description: event.location ? `at ${event.location}` : 'Event reminder',
                        triggerTime: reminderTime.toISOString(),
                    });
                }
            }
        });
    }, [events]);

    // Timezone helpers
    const setUserTimezone = (timezone: string) => {
        setUserTimezoneState(timezone);
    };

    // AI Persona helper
    const setAIPersona = (persona: AIPersonaId) => {
        setAIPersonaState(persona);
    };

    const getNowInTimezone = (): Date => {
        return new Date();
    };

    const formatTimeInTimezone = (date: Date): string => {
        return date.toLocaleTimeString('en-US', {
            timeZone: userTimezone,
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Project operations
    const addProject = (project: Omit<Project, 'id'>) => {
        const newProject = {
            ...project,
            id: generateId(),
            status: project.status || 'active' as const,
        };
        setProjects((prev) => [...prev, newProject]);
    };

    const updateProject = (id: string, updates: Partial<Project>) => {
        setProjects((prev) =>
            prev.map((p) => (p.id === id ? { ...p, ...updates } : p))
        );
    };

    const deleteProject = (id: string) => {
        setProjects((prev) => prev.filter((p) => p.id !== id));
        setTasks((prev) =>
            prev.map((t) => (t.projectId === id ? { ...t, projectId: undefined } : t))
        );
    };

    const softDeleteProject = (id: string, handleTasks: 'delete' | 'unassign') => {
        setProjects((prev) =>
            prev.map((p) => (p.id === id ? { ...p, deletedAt: new Date().toISOString() } : p))
        );

        if (handleTasks === 'delete') {
            setTasks((prev) =>
                prev.map((t) =>
                    t.projectId === id
                        ? { ...t, deletedAt: new Date().toISOString() }
                        : t
                )
            );
        } else {
            setTasks((prev) =>
                prev.map((t) => (t.projectId === id ? { ...t, projectId: undefined } : t))
            );
        }
    };

    const restoreProject = (id: string) => {
        setProjects((prev) =>
            prev.map((p) => (p.id === id ? { ...p, deletedAt: undefined } : p))
        );
    };

    const permanentlyDeleteProject = (id: string) => {
        setProjects((prev) => prev.filter((p) => p.id !== id));
        setTasks((prev) =>
            prev.map((t) => (t.projectId === id ? { ...t, projectId: undefined } : t))
        );
    };

    const archiveProject = (id: string) => {
        setProjects((prev) =>
            prev.map((p) =>
                p.id === id
                    ? { ...p, status: 'archived' as const, archivedAt: new Date().toISOString() }
                    : p
            )
        );
    };

    const unarchiveProject = (id: string) => {
        setProjects((prev) =>
            prev.map((p) =>
                p.id === id
                    ? { ...p, status: 'active' as const, archivedAt: undefined }
                    : p
            )
        );
    };

    const getProject = (id: string) => {
        return projects.find((p) => p.id === id);
    };

    // Task operations
    const addTask = (task: Omit<Task, 'id'>) => {
        const newTask = { ...task, id: generateId() };
        setTasks((prev) => [...prev, newTask]);
    };

    const updateTask = (id: string, updates: Partial<Task>) => {
        setTasks((prev) =>
            prev.map((t) => (t.id === id ? { ...t, ...updates } : t))
        );
    };

    const deleteTask = (id: string) => {
        setTasks((prev) => prev.filter((t) => t.id !== id));
    };

    const softDeleteTask = (id: string) => {
        setTasks((prev) =>
            prev.map((t) => (t.id === id ? { ...t, deletedAt: new Date().toISOString() } : t))
        );
    };

    const restoreTask = (id: string) => {
        setTasks((prev) =>
            prev.map((t) => (t.id === id ? { ...t, deletedAt: undefined } : t))
        );
    };

    const permanentlyDeleteTask = (id: string) => {
        setTasks((prev) => prev.filter((t) => t.id !== id));
    };

    const toggleTaskComplete = (id: string) => {
        setTasks((prev) => {
            const taskToToggle = prev.find(t => t.id === id);
            if (!taskToToggle) return prev;

            const isCompleting = !taskToToggle.completed;

            // AUTO-CAPTURE: Memorize high-priority task completions in Second Brain
            if (isCompleting && taskToToggle.priority === 'high' && user?.id) {
                console.log(`[AppContext] Auto-capturing high-priority task: ${taskToToggle.title}`);
                saveVectorMemory(user.id, {
                    type: 'win',
                    title: `Completed: ${taskToToggle.title}`,
                    content: taskToToggle.description || `Successfully completed high-priority task: ${taskToToggle.title}`,
                    category: (taskToToggle.context.toLowerCase() as any) || 'work',
                    tags: ['achievement', 'high-priority', taskToToggle.context],
                    source: 'manual',
                    sourceDate: new Date().toISOString().split('T')[0],
                    importance: 'high'
                }).catch(err => console.error('[AppContext] Failed to auto-capture task:', err));
            }

            let newTasks = [...prev];
            if (isCompleting && taskToToggle.recurrence) {
                const nextDueDate = calculateNextDueDate(taskToToggle.dueDate, taskToToggle.recurrence);

                const shouldGenerate = !taskToToggle.recurrence.endDate ||
                    nextDueDate <= taskToToggle.recurrence.endDate;

                if (shouldGenerate) {
                    const nextTask: Task = {
                        id: generateId(),
                        title: taskToToggle.title,
                        description: taskToToggle.description,
                        completed: false,
                        priority: taskToToggle.priority,
                        timeStart: taskToToggle.timeStart,
                        timeEnd: taskToToggle.timeEnd,
                        projectId: taskToToggle.projectId,
                        context: taskToToggle.context,
                        contextColor: taskToToggle.contextColor,
                        contextBgColor: taskToToggle.contextBgColor,
                        dueDate: nextDueDate,
                        subtasks: [],
                        recurrence: taskToToggle.recurrence,
                        parentTaskId: taskToToggle.parentTaskId || taskToToggle.id,
                    };
                    newTasks.push(nextTask);
                }
            }

            return newTasks.map((t) => {
                if (t.id !== id) return t;
                return {
                    ...t,
                    completed: isCompleting,
                    completedAt: isCompleting ? new Date().toISOString() : undefined,
                };
            });
        });
    };

    const calculateNextDueDate = (currentDueDate: string | undefined, recurrence: Task['recurrence']): string => {
        const today = new Date();
        const baseDate = currentDueDate ? new Date(currentDueDate + 'T00:00:00') : today;
        const interval = recurrence?.interval || 1;

        switch (recurrence?.pattern) {
            case 'daily': {
                baseDate.setDate(baseDate.getDate() + interval);
                break;
            }
            case 'weekdays': {
                let nextDate = new Date(baseDate);
                nextDate.setDate(nextDate.getDate() + 1);
                while (nextDate.getDay() === 0 || nextDate.getDay() === 6) {
                    nextDate.setDate(nextDate.getDate() + 1);
                }
                return nextDate.toISOString().split('T')[0];
            }
            case 'weekly': {
                baseDate.setDate(baseDate.getDate() + (7 * interval));
                break;
            }
            case 'monthly': {
                baseDate.setMonth(baseDate.getMonth() + interval);
                break;
            }
            case 'custom': {
                if (recurrence?.daysOfWeek && recurrence.daysOfWeek.length > 0) {
                    const currentDay = baseDate.getDay();
                    const sortedDays = [...recurrence.daysOfWeek].sort((a, b) => a - b);
                    let nextDay = sortedDays.find(d => d > currentDay);
                    if (nextDay === undefined) {
                        nextDay = sortedDays[0];
                        baseDate.setDate(baseDate.getDate() + (7 - currentDay + nextDay));
                    } else {
                        baseDate.setDate(baseDate.getDate() + (nextDay - currentDay));
                    }
                } else {
                    baseDate.setDate(baseDate.getDate() + 7);
                }
                break;
            }
            default:
                baseDate.setDate(baseDate.getDate() + 1);
        }

        return baseDate.toISOString().split('T')[0];
    };

    const getTasksByProject = (projectId: string) => {
        return tasks.filter((t) => t.projectId === projectId && !t.deletedAt);
    };

    const activeTasks = tasks.filter((t) => !t.deletedAt);
    const trashedTasks = tasks.filter((t) => t.deletedAt);

    const activeProjects = projects.filter((p) => !p.deletedAt && p.status !== 'archived');
    const archivedProjects = projects.filter((p) => !p.deletedAt && p.status === 'archived');
    const trashedProjects = projects.filter((p) => p.deletedAt);

    // Subtask operations
    const addSubtask = (taskId: string, title: string) => {
        const newSubtask = { id: generateId(), title, completed: false };
        setTasks((prev) =>
            prev.map((t) =>
                t.id === taskId
                    ? { ...t, subtasks: [...(t.subtasks || []), newSubtask] }
                    : t
            )
        );
    };

    const updateSubtask = (taskId: string, subtaskId: string, updates: Partial<Subtask>) => {
        setTasks((prev) =>
            prev.map((t) =>
                t.id === taskId
                    ? {
                        ...t,
                        subtasks: (t.subtasks || []).map((s) =>
                            s.id === subtaskId ? { ...s, ...updates } : s
                        ),
                    }
                    : t
            )
        );
    };

    const deleteSubtask = (taskId: string, subtaskId: string) => {
        setTasks((prev) =>
            prev.map((t) =>
                t.id === taskId
                    ? { ...t, subtasks: (t.subtasks || []).filter((s) => s.id !== subtaskId) }
                    : t
            )
        );
    };

    const toggleSubtaskComplete = (taskId: string, subtaskId: string) => {
        setTasks((prev) =>
            prev.map((t) =>
                t.id === taskId
                    ? {
                        ...t,
                        subtasks: (t.subtasks || []).map((s) =>
                            s.id === subtaskId ? { ...s, completed: !s.completed } : s
                        ),
                    }
                    : t
            )
        );
    };

    // Event operations
    const refreshGoogleEvents = async (start: Date, end: Date) => {
        if (!isServiceAuthorized('calendar')) return;

        try {
            const googleEvents = await listGoogleEvents(start.toISOString(), end.toISOString());
            const mappedEvents = googleEvents.map(mapGoogleEventToAppEvent);

            setEvents(prev => {
                const formatYMD = (d: Date) => {
                    const year = d.getFullYear();
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                };

                const startStr = formatYMD(start);
                const endStr = formatYMD(end);

                const isEventInRange = (e: CalendarEvent) => {
                    if (!e.date) return false;
                    return e.date >= startStr && e.date <= endStr;
                };

                const newIds = new Set(mappedEvents.map(me => me.id));

                const keptEvents = prev.filter(e => {
                    if (!e.googleEventId) return true;
                    if (newIds.has(e.id)) return false;
                    return !isEventInRange(e);
                });

                return [...keptEvents, ...mappedEvents];
            });
        } catch (error) {
            console.error('Failed to sync Google Calendar:', error);
        }
    };

    const addEvent = async (event: Omit<CalendarEvent, 'id'>) => {
        const newEvent = { ...event, id: generateId() };
        setEvents((prev) => [...prev, newEvent]);

        if (isServiceAuthorized('calendar')) {
            try {
                const eventDate = event.date ? new Date(event.date) : new Date();
                const googlePayload = mapAppEventToGoogleEvent(newEvent as CalendarEvent, eventDate);
                const created = await createGoogleEvent(googlePayload);
                setEvents(prev => prev.map(e =>
                    e.id === newEvent.id
                        ? { ...e, googleEventId: created.id }
                        : e
                ));
            } catch (error) {
                console.error('Failed to create Google event:', error);
            }
        }
    };

    const updateEvent = async (id: string, updates: Partial<CalendarEvent>) => {
        setEvents((prev) =>
            prev.map((e) => (e.id === id ? { ...e, ...updates } : e))
        );

        const eventToUpdate = events.find(e => e.id === id);
        if (eventToUpdate && eventToUpdate.googleEventId && isServiceAuthorized('calendar')) {
            try {
                const merged = { ...eventToUpdate, ...updates };
                const eventDate = merged.date ? new Date(merged.date) : new Date();
                const googlePayload = mapAppEventToGoogleEvent(merged, eventDate);
                await updateGoogleEvent(eventToUpdate.googleEventId, googlePayload);
            } catch (error) {
                console.error('Failed to update Google event:', error);
            }
        }
    };

    const deleteEvent = async (id: string) => {
        const eventToDelete = events.find(e => e.id === id);
        setEvents((prev) => prev.filter((e) => e.id !== id));

        if (eventToDelete && eventToDelete.googleEventId && isServiceAuthorized('calendar')) {
            try {
                await deleteGoogleEvent(eventToDelete.googleEventId);
            } catch (error) {
                console.error('Failed to delete Google event:', error);
            }
        }
    };

    const softDeleteEvent = (id: string) => {
        setEvents((prev) =>
            prev.map((e) => (e.id === id ? { ...e, deletedAt: new Date().toISOString() } : e))
        );
    };

    const restoreEvent = (id: string) => {
        setEvents((prev) =>
            prev.map((e) => (e.id === id ? { ...e, deletedAt: undefined } : e))
        );
    };

    const permanentlyDeleteEvent = async (id: string) => {
        await deleteEvent(id);
    };

    const activeEvents = events.filter((e) => !e.deletedAt);
    const trashedEvents = events.filter((e) => e.deletedAt);

    // Inbox operations
    const addInboxItem = (item: Omit<InboxItem, 'id'>) => {
        const newItem = { ...item, id: generateId() };
        setInboxItems((prev) => [newItem, ...prev]);
    };

    const markInboxItemRead = (id: string) => {
        setInboxItems((prev) =>
            prev.map((i) => (i.id === id ? { ...i, read: true } : i))
        );
    };

    const deleteInboxItem = (id: string) => {
        setInboxItems((prev) => prev.filter((i) => i.id !== id));
    };

    const refreshGmailItems = useCallback(async () => {
        if (!isServiceAuthorized('gmail')) return;
        try {
            const messages = await listGmailMessages('is:inbox', 30);
            const allGmailItems = messages.map(mapGmailMessageToInboxItem);
            const { triageEmails } = await import('../services/aiService');
            const triageResults = await triageEmails(
                allGmailItems.map(item => ({
                    id: item.id,
                    sender: item.sender || '',
                    subject: item.subject,
                    preview: item.preview || '',
                }))
            );
            const triageMap = new Map(triageResults.map(r => [r.id, r]));
            const actionableEmails = allGmailItems
                .filter(item => {
                    const triage = triageMap.get(item.id);
                    return triage?.isActionable && triage?.isFromHuman;
                })
                .map(item => {
                    const triage = triageMap.get(item.id);
                    const mappedPriority = triage?.priority === 'low' ? 'normal' :
                        (triage?.priority as 'high' | 'medium' | 'normal' | undefined) || item.priority;
                    return {
                        ...item,
                        priority: mappedPriority,
                        category: triage?.priority === 'high' ? 'urgent' as const : 'today' as const,
                        aiLabel: triage?.actionType?.replace('_', ' ') || undefined,
                    };
                });
            setInboxItems(prev => {
                const nonGmail = prev.filter(i => i.source !== 'gmail');
                return [...actionableEmails, ...nonGmail].sort((a, b) =>
                    new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                );
            });
        } catch (error) {
            console.error('Failed to refresh Gmail items:', error);
        }
    }, []);

    // Habit functions
    const addHabit = (habit: Omit<Habit, 'id' | 'completedDates' | 'currentStreak' | 'longestStreak' | 'createdAt'>) => {
        const newHabit: Habit = {
            ...habit,
            id: generateId(),
            completedDates: [],
            currentStreak: 0,
            longestStreak: 0,
            createdAt: new Date().toISOString(),
        };
        setHabits((prev) => [...prev, newHabit]);
    };

    const updateHabit = (id: string, updates: Partial<Habit>) => {
        setHabits((prev) => prev.map((h) => (h.id === id ? { ...h, ...updates } : h)));
    };

    const deleteHabit = (id: string) => {
        setHabits((prev) => prev.map((h) => (h.id === id ? { ...h, deletedAt: new Date().toISOString() } : h)));
    };

    const toggleHabitComplete = (id: string, date: string) => {
        setHabits((prev) =>
            prev.map((habit) => {
                if (habit.id !== id) return habit;
                let completedDates = [...habit.completedDates];
                const isCompleted = completedDates.includes(date);
                if (isCompleted) {
                    completedDates = completedDates.filter((d) => d !== date);
                } else {
                    completedDates.push(date);
                    completedDates.sort();
                }
                const { currentStreak, longestStreak } = calculateStreaks(completedDates, habit.frequency);
                return {
                    ...habit,
                    completedDates,
                    currentStreak,
                    longestStreak: Math.max(habit.longestStreak, longestStreak),
                };
            })
        );
    };

    const calculateStreaks = (dates: string[], _frequency: Habit['frequency']): { currentStreak: number; longestStreak: number } => {
        if (dates.length === 0) return { currentStreak: 0, longestStreak: 0 };
        const sortedDates = [...dates].sort().reverse();
        const today = new Date().toISOString().split('T')[0];
        const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
        let currentStreak = 0;
        let longestStreak = 0;
        let tempStreak = 0;
        if (sortedDates[0] === today || sortedDates[0] === yesterday) {
            currentStreak = 1;
            for (let i = 1; i < sortedDates.length; i++) {
                const prevDate = new Date(sortedDates[i - 1]);
                const currDate = new Date(sortedDates[i]);
                const diffDays = Math.floor((prevDate.getTime() - currDate.getTime()) / 86400000);
                if (diffDays === 1) currentStreak++;
                else break;
            }
        }
        tempStreak = 1;
        longestStreak = 1;
        const allSorted = [...dates].sort();
        for (let i = 1; i < allSorted.length; i++) {
            const prevDate = new Date(allSorted[i - 1]);
            const currDate = new Date(allSorted[i]);
            const diffDays = Math.floor((currDate.getTime() - prevDate.getTime()) / 86400000);
            if (diffDays === 1) {
                tempStreak++;
                longestStreak = Math.max(longestStreak, tempStreak);
            } else if (diffDays > 1) {
                tempStreak = 1;
            }
        }
        return { currentStreak, longestStreak };
    };

    const activeHabits = habits.filter((h) => !h.deletedAt);

    // Goal functions
    const addGoal = (goalData: Omit<Goal, 'id' | 'progress' | 'status' | 'createdAt' | 'milestones'> & { milestones?: string[] }) => {
        const newGoal: Goal = {
            id: generateId(),
            title: goalData.title,
            description: goalData.description,
            category: goalData.category,
            icon: goalData.icon,
            color: goalData.color,
            targetDate: goalData.targetDate,
            milestones: (goalData.milestones || []).map(title => ({
                id: generateId(),
                title,
                completed: false,
            })),
            progress: 0,
            status: 'active',
            createdAt: new Date().toISOString(),
        };
        setGoals(prev => [...prev, newGoal]);
    };

    const updateGoal = (id: string, updates: Partial<Goal>) => {
        setGoals(prev => prev.map(g => (g.id === id ? { ...g, ...updates } : g)));
    };

    const deleteGoal = (id: string) => {
        setGoals(prev => prev.map(g => (g.id === id ? { ...g, deletedAt: new Date().toISOString() } : g)));
    };

    const toggleMilestone = (goalId: string, milestoneId: string) => {
        setGoals(prev => prev.map(goal => {
            if (goal.id !== goalId) return goal;
            const updatedMilestones = goal.milestones.map(m => {
                if (m.id !== milestoneId) return m;
                return {
                    ...m,
                    completed: !m.completed,
                    completedAt: !m.completed ? new Date().toISOString() : undefined,
                };
            });
            const completedCount = updatedMilestones.filter(m => m.completed).length;
            const progress = updatedMilestones.length > 0
                ? Math.round((completedCount / updatedMilestones.length) * 100)
                : 0;
            const isComplete = progress === 100;
            return {
                ...goal,
                milestones: updatedMilestones,
                progress,
                status: isComplete ? 'completed' : goal.status,
                completedAt: isComplete && !goal.completedAt ? new Date().toISOString() : goal.completedAt,
            };
        }));
    };

    const addMilestone = (goalId: string, title: string) => {
        setGoals(prev => prev.map(goal => {
            if (goal.id !== goalId) return goal;
            const newMilestone: GoalMilestone = { id: generateId(), title, completed: false };
            const updatedMilestones = [...goal.milestones, newMilestone];
            const completedCount = updatedMilestones.filter(m => m.completed).length;
            const progress = Math.round((completedCount / updatedMilestones.length) * 100);
            return {
                ...goal,
                milestones: updatedMilestones,
                progress,
                status: progress < 100 ? 'active' : goal.status,
            };
        }));
    };

    const activeGoals = goals.filter(g => !g.deletedAt);

    return (
        <AppContext.Provider
            value={{
                projects: activeProjects,
                archivedProjects,
                trashedProjects,
                addProject,
                updateProject,
                deleteProject,
                softDeleteProject,
                restoreProject,
                permanentlyDeleteProject,
                archiveProject,
                unarchiveProject,
                getProject,
                tasks: activeTasks,
                trashedTasks,
                addTask,
                updateTask,
                deleteTask,
                softDeleteTask,
                restoreTask,
                permanentlyDeleteTask,
                toggleTaskComplete,
                getTasksByProject,
                addSubtask,
                updateSubtask,
                deleteSubtask,
                toggleSubtaskComplete,
                events: activeEvents,
                trashedEvents,
                refreshGoogleEvents,
                addEvent,
                updateEvent,
                deleteEvent,
                softDeleteEvent,
                restoreEvent,
                permanentlyDeleteEvent,
                inboxItems,
                addInboxItem,
                markInboxItemRead,
                deleteInboxItem,
                refreshGmailItems,
                userTimezone,
                setUserTimezone,
                getNowInTimezone,
                formatTimeInTimezone,
                aiPersona,
                setAIPersona,
                habits: activeHabits,
                addHabit,
                updateHabit,
                deleteHabit,
                toggleHabitComplete,
                goals: activeGoals,
                addGoal,
                updateGoal,
                deleteGoal,
                toggleMilestone,
                addMilestone,
            }}
        >
            {children}
        </AppContext.Provider>
    );
}

export function useAppState() {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAppState must be used within an AppProvider');
    }
    return context;
}
